import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { NameInput } from '@mobi/account/Common'
import { signupTracker } from '@mobi/account/Common/Analytics'
import { AccountHeader } from '@mobi/component-library/Areas/Account'
import { Button } from '@mobi/component-library/Base/Buttons'
import { StepGroup } from '@mobi/component-library/Base/StepGroup'
import { InfoBox } from '@mobi/component-library/Feedback/InfoBox'
import { Checkbox, Input } from '@mobi/component-library/Selection&Input/Inputs'
import { useApplicationCheck } from '../Hooks'
import {
  getSignUpState,
  setHasUserStartedSignup,
  setSignupApplication,
  setSignupVerification,
  setUserKnownByOtherNames,
} from '../Store'
import {
  getDisplayBirthdateFromISO,
  getFormattedBirthdate,
  getISOBirthdate,
  isValidName,
  routes,
  validateBirthdate,
} from '../Utils'
import { BoldStyled, FooterStyled, FormSection, PersonalDetailsDisclaimer, SignupError } from './'
import { SignUpPageStyled } from './SignUpPage.styled'

export const PersonalDetails = () => {
  const navigate = useNavigate()
  const [showErrorPopup, setShowErrorPopup] = React.useState(false)
  const state = useSelector(getSignUpState)
  const application = state.signupApplication
  const verification = state.signupVerification
  const dispatch = useDispatch()
  const [birthdate, setBirthdate] = React.useState(
    getDisplayBirthdateFromISO(application.birthdate)
  )

  const { mutate: checkApplication, isPending: isCheckPending } = useApplicationCheck({
    onSuccess: data => {
      dispatch(
        setSignupVerification({
          ...verification,
          duplicateExists: data.duplicateExists,
          isDuplicateClosed: (data.accountStatus?.toLowerCase() ?? '') === 'closed',
        })
      )
      if (data.duplicateExists) {
        navigate(routes.signUpUnsuccessful)
        return
      }
      signupTracker({
        interaction_type: 'Name & DOB',
        signUpFlow: state.isVerificationAccount ? 'ID Verification' : 'Standard',
        alias: state.isUserKnownByOtherNames ? true : false,
      })
      navigate(routes.locationDetails)
    },
    onError: () => setShowErrorPopup(true),
  })

  const handleNext = () => {
    checkApplication({
      lastName: application.surname,
      birthdate: application.birthdate,
    })
  }

  const handleBirthdateBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const validationMessage = validateBirthdate(
      getFormattedBirthdate(e.target.value, birthdate, e.target.selectionStart)
    )
    if (validationMessage !== '') {
      setValidationMessages(prev => ({
        ...prev,
        birthdate: validationMessage,
      }))
      dispatch(
        setSignupApplication({
          ...application,
          birthdate: '',
        })
      )
    } else {
      if (birthdate !== '') {
        dispatch(
          setSignupApplication({
            ...application,
            birthdate: getISOBirthdate(birthdate),
          })
        )
      }
    }
  }

  const isValidKnownByOtherNames =
    !state.isUserKnownByOtherNames ||
    isValidName(application.firstNameAlias) ||
    isValidName(application.lastNameAlias)

  const [validationMessages, setValidationMessages] = React.useState<Record<string, string>>({})

  const isOver18 = !!birthdate && !validationMessages.birthdate
  const hasValidEntries = Object.values(validationMessages).every(error => error === '')
  const isComplete =
    hasValidEntries &&
    birthdate.length > 0 &&
    isValidName(application.firstName) &&
    isValidName(application.middleName, true) &&
    isValidName(application.surname) &&
    isValidKnownByOtherNames

  const resetFieldValidation = (field: keyof typeof validationMessages) =>
    setValidationMessages(prev => ({
      ...prev,
      [field]: '',
    }))

  const validateIsKnownByOtherNames = () => {
    setValidationMessages(prev => ({
      ...prev,
      isKnownByOtherNames:
        state.isUserKnownByOtherNames &&
        !isValidName(application.firstNameAlias ?? '') &&
        !isValidName(application.lastNameAlias ?? '')
          ? 'Please enter details or untick the box.'
          : '',
    }))
  }

  return (
    <>
      <SignUpPageStyled>
        <StepGroup currentStep={0} totalSteps={5} />
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            color='secondary_grey'
            onClick={() => {
              signupTracker({
                interaction_type: 'Already Have Account',
                signUpFlow: state.isVerificationAccount ? 'ID Verification' : 'Standard',
                signUpStarted: state.hasUserStartedSignup,
              })

              navigate('/account')
            }}
            type='button'
          >
            <span>
              Already have a <BoldStyled>TABtouch account?</BoldStyled>
            </span>
          </Button>
        </div>
        <AccountHeader
          alignItems='start'
          title="Let's get you started!"
          description={
            <InfoBox color='blue' size='sm' hideIcon={true}>
              Enter your full name and DOB as it appears on your ID documents (including middle name
              if applicable)
            </InfoBox>
          }
          style={{ marginBottom: 0 }}
        />

        <FormSection>
          <Input
            label='Date of Birth'
            name='birthdate'
            placeholder='DD/MM/YYYY'
            required
            maxLength={10}
            value={birthdate}
            onChange={e => {
              setBirthdate(
                getFormattedBirthdate(e.target.value, birthdate, e.target.selectionStart)
              )
            }}
            onFocus={() => {
              resetFieldValidation('birthdate')
              if (!state.hasUserStartedSignup) {
                signupTracker({
                  interaction_type: 'Sign Up Started',
                  signUpFlow: state.isVerificationAccount ? 'ID Verification' : 'Standard',
                })
                dispatch(setHasUserStartedSignup(true))
              }
            }}
            onBlur={e => handleBirthdateBlur(e)}
            isInvalid={!!validationMessages.birthdate}
            helpText={
              validationMessages.birthdate ||
              'Must match your ID documents · You must be 18 or over to wager in Australia'
            }
          />
          <NameInput
            firstName={application.firstName}
            middleName={application.middleName}
            lastName={application.surname}
            setFirstName={firstName =>
              dispatch(
                setSignupApplication({
                  ...application,
                  firstName,
                })
              )
            }
            setMiddleName={middleName =>
              dispatch(
                setSignupApplication({
                  ...application,
                  middleName,
                })
              )
            }
            setLastName={surname =>
              dispatch(
                setSignupApplication({
                  ...application,
                  surname,
                })
              )
            }
            isDisabled={!isOver18}
          />

          <Checkbox
            id='sign-up-known-by-other-names'
            name='isKnownByOtherNames'
            checked={state.isUserKnownByOtherNames}
            label='Are you known by any other names?'
            onChange={e => {
              dispatch(setUserKnownByOtherNames(e.target.checked))
              dispatch(
                setSignupApplication({
                  ...application,
                  firstNameAlias: '',
                  lastNameAlias: '',
                })
              )
              resetFieldValidation('isKnownByOtherNames')
            }}
            isDisabled={!isOver18}
          />

          {state.isUserKnownByOtherNames && (
            <>
              <Input
                type='text'
                label='First name (if applicable)'
                name='firstNameAlias'
                placeholder='Enter first name'
                value={application.firstNameAlias}
                onChange={e =>
                  dispatch(
                    setSignupApplication({
                      ...application,
                      firstNameAlias: e.target.value,
                    })
                  )
                }
                onBlur={validateIsKnownByOtherNames}
                isInvalid={!!validationMessages.isKnownByOtherNames}
              />
              <Input
                type='text'
                label='Last name (if applicable)'
                name='lastNameAlias'
                placeholder='Enter last name'
                value={application.lastNameAlias}
                onChange={e =>
                  dispatch(
                    setSignupApplication({
                      ...application,
                      lastNameAlias: e.target.value,
                    })
                  )
                }
                onBlur={validateIsKnownByOtherNames}
                helpText={validationMessages.isKnownByOtherNames}
                isInvalid={!!validationMessages.isKnownByOtherNames}
              />
            </>
          )}
        </FormSection>

        <FooterStyled>
          <>
            <Button
              type='button'
              isFullWidth
              isLoading={isCheckPending}
              disabled={!isComplete || !isOver18}
              onClick={handleNext}
            >
              Next
            </Button>
            <InfoBox color='grey' size='md' hideIcon={true}>
              <PersonalDetailsDisclaimer />
            </InfoBox>
          </>
        </FooterStyled>
      </SignUpPageStyled>
      <SignupError isOpen={showErrorPopup} onClose={() => setShowErrorPopup(false)} />
    </>
  )
}
